/**
 * Domain-based branding.
 *
 * The same build is served on several domains (wealthlogs.com, app.plastikan.com, ...).
 * The brand is picked at runtime from window.location.hostname, so adding a new
 * white-label domain only needs a new entry here plus the domain in Coolify.
 *
 * Per-business details that appear on documents (company name, address, logo, VAT...)
 * stay in the user's Settings; this file only controls the app shell.
 */

const DEFAULT_BRAND = {
    key: 'wealthlogs',
    name: 'WealthLogs',
    title: 'WealthLogs - Invoicing & Payments',
    logoUrl: null,           // null → generic document icon
    faviconUrl: null,
    primaryColor: '#4f46e5', // indigo-600
    showMarketingLanding: true,
    allowRegistration: true,
    homeUrl: null,           // external marketing site, shown as "back" link on login
};

const BRANDS = {
    plastikan: {
        key: 'plastikan',
        name: 'Plastikan',
        title: 'Plastikan Backoffice',
        logoUrl: null, // e.g. '/brands/plastikan/logo.png' (put the file in public/brands/plastikan/)
        faviconUrl: null,
        primaryColor: '#093c71', // MINZEN / MEPER navy
        // Accent scale ("R G B") replacing the default indigo across the app
        palette: {
            50: '238 244 251',
            100: '214 228 244',
            200: '173 200 232',
            300: '143 176 216',
            400: '86 131 189',
            500: '26 85 150',
            600: '9 60 113',
            700: '7 49 93',
            800: '5 37 72',
            900: '4 27 53',
        },
        showMarketingLanding: false,
        allowRegistration: false,
        homeUrl: 'https://plastikan.com',
    },
};

// hostname → brand key. Subdomains are matched too (app.plastikan.com → plastikan.com).
const DOMAINS = {
    'plastikan.com': 'plastikan',
};

const resolveBrandKey = (hostname) => {
    // ?brand=plastikan lets you preview a brand locally (localhost only)
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        const preview = new URLSearchParams(window.location.search).get('brand');
        if (preview && BRANDS[preview]) return preview;
    }
    const match = Object.keys(DOMAINS).find(
        (domain) => hostname === domain || hostname.endsWith(`.${domain}`)
    );
    return match ? DOMAINS[match] : null;
};

const brandKey = typeof window !== 'undefined' ? resolveBrandKey(window.location.hostname) : null;

export const brand = { ...DEFAULT_BRAND, ...(brandKey ? BRANDS[brandKey] : {}) };

/**
 * Apply brand to the document head (title, favicon, theme color).
 */
export const applyBrandToDocument = () => {
    document.title = brand.title;
    document.documentElement.style.setProperty('--brand-primary', brand.primaryColor);
    Object.entries(brand.palette || {}).forEach(([shade, rgb]) => {
        document.documentElement.style.setProperty(`--brand-${shade}`, rgb);
    });

    if (brand.faviconUrl) {
        let link = document.querySelector("link[rel='icon']");
        if (!link) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.head.appendChild(link);
        }
        link.href = brand.faviconUrl;
    }

    let themeColor = document.querySelector("meta[name='theme-color']");
    if (!themeColor) {
        themeColor = document.createElement('meta');
        themeColor.name = 'theme-color';
        document.head.appendChild(themeColor);
    }
    themeColor.content = brand.primaryColor;
};
